import { useCallback, useMemo, useRef, useState } from 'react';
import useShortcut from '../../../services/shortcuts/useShortcut';
import {
  createChangeItemSelectionState,
  createHandleToggleItem,
  createShiftSelectItem,
} from '../../playlist/playlistCommon';

interface Params<T> {
  /**
   * What ctrl+A selects. Omit when useMoreSharedPlaylistMethods follows:
   * it registers the same shortcut over the filtered, sorted view, and being
   * mounted later, it is the one that gets the key.
   */
  tracks?: T[];
  playlistElement: HTMLElement | undefined;
}
export default function useSharedPlaylistMethods<T>({ tracks, playlistElement }: Params<T>) {
  // Current recommendation - 2020-04-27
  // Stable, mutable Set instance wrapped in a mutable wrapper.
  // Take care not to recreate Set when updating to ensure all handlers point to the same Set.
  //
  // Original: setState with the same set would not re-render due to reference equality
  // creating a new set on each update could be expensive
  // Thus we do like this, creating a wrapper object on each update
  //
  // Edit: Is not expensive, should refactor, we still need both of these however
  // Reasoning for refobject below.
  // To ensure non-equality of references all setSelectedItems must use a fresh { current: }
  // wrapper.
  // Components that depend on selectedItems for displaying selected state must of course
  // ensure to have this object assigned as prop since they would not re-render when
  // solely depending on the refobject below.
  //
  // Edit2: Immutability for the set causes it's own problems.
  // If we recreate the set on each change that means we can not create handleToggleItem below
  // and expect that a call to ensureIsSelected that calls setSelectedItems will mean
  // that handleToggleItem's React.MutableRefObject points to the newly created Set
  // since a ensureIsSelected(handleToggleItem) call will invoke toggle before react
  // updates the ref..
  const [selectedItems, setSelectedItems] = useState({
    current: new Set<T>(),
  });
  // Yep this all seems really redundant but in fact it isn't
  // More importantly, this ref object is assigned to ctx.DnD data which definately needs
  // a stable refobject to dereference when it comes to handling onDrop events
  const selectedItemsRef = useRef(selectedItems.current);
  selectedItemsRef.current = selectedItems.current;

  const lastSelectedItem = useRef<T>(undefined);

  const handleToggleItem = useMemo(
    () => createHandleToggleItem(setSelectedItems, lastSelectedItem),
    [],
  );
  const changeItemSelectionState = useMemo(
    () => createChangeItemSelectionState(setSelectedItems, lastSelectedItem),
    [],
  );
  const shiftSelectItem = useMemo(
    () => createShiftSelectItem(setSelectedItems, lastSelectedItem),
    [],
  );

  const clearSelected = useCallback(function () {
    setSelectedItems({
      current: new Set(),
    });
  }, []);

  useShortcut(
    {
      id: 'select-all',
      keys: { key: 'a', mod: true },
      group: 'Library',
      description: 'Select all tracks in view',
      run: () => {
        setSelectedItems({
          current: new Set(tracks),
        });
      },
    },
    tracks !== undefined,
  );

  // A dialog on top registered later and gets Escape first; a playlist view
  // registers its own version of this over the filter box as well.
  useShortcut({
    id: 'clear-selection',
    keys: { key: 'Escape' },
    group: 'Library',
    description: 'Clear the selection',
    run: () => {
      if (selectedItemsRef.current.size === 0) {
        return false;
      }

      clearSelected();
    },
  });

  return {
    lastSelectedItem,
    selectedItems,
    selectedItemsRef,

    handleToggleItem,
    shiftSelectItem,
    changeItemSelectionState,
    clearSelected,

    setSelectedItems,
  };
}
